import { JSX, cloneElement, useMemo } from 'react';

import { useApplicationContext } from 'shared/context/ApplicationContext';
import { useCardOfferDataContext } from 'shared/context/CardContext';
import { useCAForCard } from 'shared/hooks/useCAForCard';
import { ELabels } from 'shared/repositories/commercial-search-offers/entities/offer_extended_properties/OfferExtendedPropertiesSchema';
import { memoWithDisplayName } from 'shared/utils/memoWithDisplayName';

import { WrapElementsSpacer } from '../WrapElementsSpacer';

import { BestPlaceAnalyticsLabelContainer } from './internal/components/BestPlaceAnalyticsLabel';
import { CoworkingsBenefitsLabel } from './internal/components/CoworkingsBenefitsLabel';
import { ElectronicTradingLabel } from './internal/components/ElectronicTradingLabel';
import { FromOwnerLabel } from './internal/components/FromOwnerLabel';
import { GoodPriceLabel } from './internal/components/GoodPriceLabel';
import { UniqueForCianLabel } from './internal/components/UniqueForCianLabel';
import { PublisherLabelTypes } from './types';

/**
 * @description Данная конструкция нужна для того, что бы логировать случаи, когда бекенд присылает лейбл, но фронтенд
 *   о нём не знает
 */
const unknownLoggedLabels = new Set<PublisherLabelTypes>();

export const PublisherLabelsContainer = memoWithDisplayName(() => {
  const { logger, config } = useApplicationContext();
  const {
    extendedProperties: { labels },
  } = useCardOfferDataContext();
  /**
   * @todo Удалить фича-тогл
   * @description Данный функционал появился в задаче CD-187914, будет удалён в задаче CD-188067
   */
  const electronicTradingEnabled = config.getStrict<boolean>('Commercial.ElectronicTrading.Enabled');

  const ca = useCAForCard();

  const labelsViews = useMemo(() => {
    return labels.reduce((accumulator, label) => {
      const labelView = ((): JSX.Element | undefined => {
        const defaultTrackingData = { action: 'click_sopr', category: 'Listing' } as const;

        switch (label) {
          case ELabels.GoodPrice:
            return <GoodPriceLabel trackLabelClick={(): void => ca({ ...defaultTrackingData, label: 'good_price' })} />;
          case ELabels.UniqueForCian:
            return (
              <UniqueForCianLabel
                trackLabelClick={(): void => ca({ ...defaultTrackingData, label: 'unique_on_cian' })}
              />
            );
          case ELabels.FromOwner:
            return <FromOwnerLabel trackLabelClick={(): void => ca({ ...defaultTrackingData, label: 'owner' })} />;
          case ELabels.CoworkingBenefits:
            return (
              <CoworkingsBenefitsLabel
                trackCoworkingInfoClick={(): void => ca({ ...defaultTrackingData, label: 'profitably' })}
                trackLabelClick={(): void => ca({ ...defaultTrackingData, label: 'coworking' })}
              />
            );
          case ELabels.BestPlaceAnalytics:
            return <BestPlaceAnalyticsLabelContainer />;
        }

        /**
         * @todo Удалить if-блок и ненужный "return;" при удалении фича-тогла
         * @description Данный функционал появился в задаче CD-187914, будет удалён в задаче CD-188067
         */
        if (electronicTradingEnabled) {
          switch (label) {
            case ELabels.PledgeRealty:
            case ELabels.TradeRealty:
            case ELabels.CommEsales:
            case ELabels.ElectronTradeSite:
              return <ElectronicTradingLabel type={label} />;
          }
        }

        return;
      })();

      if (labelView) {
        accumulator.push(cloneElement(labelView, { key: label }));
      } else if (!unknownLoggedLabels.has(label)) {
        logger.warning(
          `Не реализован лейбл публиканта, нужно добавить бейдж с типом: ${label} или на оборот удалить его так как визуала под этот тип нет`,
          { domain: 'src/shared/components/PublisherLabels/PublisherLabelsContainer.tsx' },
        );

        unknownLoggedLabels.add(label);
      }

      return accumulator;
    }, Array.of<JSX.Element>());
  }, [ca, labels, logger, electronicTradingEnabled]);

  return (
    <WrapElementsSpacer
      containerTestId="publisher-labels.container"
      horizontalSpace={8}
      itemTestId="publisher-labels.label"
      verticalSpace={8}
    >
      {labelsViews}
    </WrapElementsSpacer>
  );
}, 'PublisherBadgesContainer');
